/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from "react";
import { css, useTheme } from "@emotion/react";
import { Container, Table } from "@coreai/component-library";
import THeaders from "./THeader";
import TBody from "./TBody";
import { useLanguage } from "../../../LanguageContext";
import {TablePagination} from "./TablePagination";
import AccordionTBody from "./AccordionTBody";

interface DataTableProps {
    tableClassName: any;
    columns: any[];
    tableData: any[];
    handleRowClick: (rowData: any, event: React.MouseEvent) => void;
    tableRowClass: string;
    totalRows: number;
    handlePageChange: (page: number) => void;
    paginationClassName: any;
    containerClassName: string;
    isTableDataLoading: boolean;
    rowsPerPageOptions?: any[];
    defaultPage?: number;
    setPageNo?: (page: number) => void;
    handleRowsPerPageChange?: (rowsPerPage: number) => void;
    defaultRowsPerPage?: number;
    isRowAccordion?: boolean;
    no_data_found_msg?: string;
};

export interface sortingColumnsProps {
    [key: string]: string;
}

function DataTable({
    tableClassName,
    columns,
    tableData,
    handleRowClick,
    tableRowClass,
    totalRows,
    handlePageChange,
    paginationClassName,
    containerClassName,
    isTableDataLoading,
    rowsPerPageOptions,
    defaultPage,
    setPageNo,
    handleRowsPerPageChange,
    defaultRowsPerPage,
    isRowAccordion,
    no_data_found_msg,
} : DataTableProps) {
    const { t } = useLanguage();
    const theme = useTheme();
    const _no_data_found_msg = no_data_found_msg ? no_data_found_msg : t("dataTable_no_data_found");
    const tableStyleCss = useMemo(() => css`
        // min-width: 1000px;
        thead {
            th {
                background-color: #fff;
                font-size: ${theme.typography.body_semiBold};
            }
        }

        tbody {
            tr {
                background-color: #fff;
            }
        }
    `, [theme]);

const paginationStyle = css`
    padding-top: ${theme.spacing["4xl"]};
    p {
          margin-top: 0px !important;
    }
`;

const containerStyle = css`
    display: flex;
    flex-direction: column;
    background-color: ${theme.colors.white};
    padding: ${theme.spacing["xxl"]};
    text-align: left;
    border-radius: ${theme.radius.xs};
    box-shadow: ${theme.shadows.shallow};
    padding-top: ${theme.spacing["4xl"]};
    margin-top: ${theme.spacing["5xl"]};
`;

    useEffect(() => {
        // Find the first <svg> inside the ButtonIcon component    
        const icons = document.querySelectorAll("svg");
        icons.forEach((icon) => {
        // Get the "name" attribute from the <svg>
        const iconName = icon.getAttribute("name");
        if (iconName) {
            icon.setAttribute("role", "img");
            icon.setAttribute("aria-label", iconName.replace("-", " ")); // Convert "edit-01" → "edit 01"
        } else {
            icon.setAttribute("aria-hidden", "true"); // Hide if no name is available
        }
        });     
    }, [tableData]);
    return (
        <Container variant="column" css={containerStyle} className={`custom-agw-table-container ${containerClassName ?? ""}`}>
            <Table css={tableStyleCss} className={`custom-agw-table ${tableClassName ?? ""}`}>
                <THeaders headers={columns} />

                { !isTableDataLoading && tableData.length === 0 &&
                    <tbody>
                    <tr style={{ backgroundColor: "#fff !important" }}>
                        <td colSpan={columns.length} style={{ textAlign: "center" }}>
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
                                <div> {_no_data_found_msg}</div>
                            </div>
                        </td>
                    </tr>
                </tbody>                    
                }
                {
                    isTableDataLoading? (
                        <tbody>
                            <tr style={{ backgroundColor: "#fff !important" }}>
                                <td colSpan={columns.length} style={{ textAlign: "center" }}>
                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
                                        <div className="spinner"></div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    ) : isRowAccordion ? (
                        <>
                        <AccordionTBody columns={columns} tableData={tableData} handleRowClick={handleRowClick} tableRowClass={tableRowClass} />
                        </>
                    ) : (
                        <TBody columns={columns} tableData={tableData} handleRowClick={handleRowClick} tableRowClass={tableRowClass} />
                    ) 
                }
            </Table>
            { !isTableDataLoading && tableData.length > 0 &&
            <div css={[paginationStyle, paginationClassName && paginationClassName]}>
                <TablePagination
                    inputAriaLabel={t("core_page_number")}
                    selectLabel={t("datatable_results_per_page")}
                    ofText={t("datatable_of")}
                    navLabel={t("core_rowPerPage")}
                    nextPageLabel={t("datatable_next")}
                    previousPageLabel={t("datatable_previous")}                    
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={rowsPerPageOptions}
                    totalRows={totalRows}                   
                    defaultPage={defaultPage}
                    setPageNo={setPageNo}
                    defaultRowsPerPage={defaultRowsPerPage}
                />
            </div>
            }
        </Container>
    )
};

DataTable.defaultProps = {
    tableClassName: '',
    handleRowClick: () => {},
    tableRowClass: '',
    totalRows: 0,
    handlePageChange: () => {},
    handleRowsPerPageChange: () => {},
    paginationClassName: '',
    containerClassName: '',
    defaultPage:1,
};

export default DataTable;